import * as React from 'react';
import { MouseEvent } from 'react';
import {
   SearchContainer,
   SearchInput,
   Container,
   Toolbar,
   ToolbarRight,
} from './HierarchicalDevicePicker';
import { Button } from './Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons/faMagnifyingGlass';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';
import algoliasearch, { SearchClient } from 'algoliasearch/lite';
import {
   InstantSearch,
   connectHits,
   connectSearchBox,
} from 'react-instantsearch-dom';
import GridItem from './GridItem';
import { Grid } from './GridExplorer';
import { color } from '@core-ds/primitives';
import { _js } from '@ifixit/localize';
import { DevicePickerProps } from './types';
import { useDevicePickerContext } from './DevicePickerContext';
import NoResults from './NoResults';

interface AlgoliaDevicePickerProps extends DevicePickerProps {
   onSelectCategory: (category: string) => void;
   algoliaConfig: AlgoliaConfig;
}

interface AlgoliaHitsListProps {
   hits: any[];
   leaves: Set<string> | null;
   onSelectCategory: (category: string) => void;
   onSelectLeaf: (title: string) => void;
   allowOrphan?: boolean;
}

export interface AlgoliaConfig {
   apiKey: string;
   appId: string;
   indexPrefix: string;
}

export function AlgoliaDevicePicker(props: AlgoliaDevicePickerProps) {
   const searchClient = algoliasearch(
      props.algoliaConfig.appId,
      props.algoliaConfig.apiKey,
   );

   return <AlgoliaSearchRoot searchClient={searchClient} {...props} />;
}

function AlgoliaSearchRoot(
   props: AlgoliaDevicePickerProps & { searchClient: SearchClient },
) {
   const indexRoot = 'topic';
   const langid = 'en';
   const indexName = `${props.algoliaConfig.indexPrefix}${indexRoot}_${langid}`;
   const { leaves, query } = useDevicePickerContext();
   return (
      <InstantSearch searchClient={props.searchClient} indexName={indexName}>
         <Container id="devicePickerModal">
            <AlgoliaSearchBox
               defaultRefinement={query}
               onSelectLeaf={props.onSubmit}
            />
            <AlgoliaHitsList
               leaves={leaves}
               onSelectCategory={props.onSelectCategory}
               onSelectLeaf={props.onSubmit}
               allowOrphan={props.allowOrphan}
            />
            <Toolbar>
               <ToolbarRight>
                  <Button onClick={props.onCancel}>{_js('Cancel')}</Button>
               </ToolbarRight>
            </Toolbar>
         </Container>
      </InstantSearch>
   );
}

function HitsList({
   hits,
   leaves,
   onSelectCategory,
   onSelectLeaf,
   allowOrphan = true,
}: AlgoliaHitsListProps) {
   const { isUncategorized, query } = useDevicePickerContext();
   if (hits.length === 0) {
      return (
         <NoResults
            query={query}
            allowOrphan={allowOrphan}
            selectItem={onSelectLeaf}
         />
      );
   }
   return (
      <Grid>
         {hits.map((hit) => {
            return (
               <HitBox
                  key={hit.device}
                  hit={hit}
                  onSelect={
                     (leaves && leaves.has(hit.title)) ||
                     isUncategorized(hit.title)
                        ? onSelectLeaf
                        : onSelectCategory
                  }
               />
            );
         })}
      </Grid>
   );
}

interface HitBoxProps {
   hit: DeviceHit;
   onSelect: (category: string) => void;
}

function HitBox({ hit, onSelect }: HitBoxProps) {
   return (
      <GridItem
         title={hit.title}
         image={hit.image_url}
         onClick={() => onSelect(hit.device)}
      />
   );
}

function SearchBox({
   currentRefinement,
   refine,
   onSelectLeaf,
}: {
   currentRefinement: string;
   refine: any;
   onSelectLeaf: (title: string) => void;
}) {
   const { setQuery } = useDevicePickerContext();
   return (
      <SearchContainer>
         <FontAwesomeIcon
            icon={faMagnifyingGlass}
            color={color.gray[400]}
            className="fa-abs-25"
         />
         <SearchInput
            autoFocus
            value={currentRefinement}
            onChange={(event) => {
               const searchQuery = event.target.value;
               setQuery(searchQuery);
               refine(searchQuery);
            }}
            id="devicePickerSearch"
         />
         <Button
            style={{ marginRight: '21px' }}
            onClick={(event: MouseEvent) => {
               event.preventDefault();
               onSelectLeaf(currentRefinement);
            }}
         >
            <FontAwesomeIcon icon={faPlus} className="fa-solid" />
         </Button>
      </SearchContainer>
   );
}

const AlgoliaSearchBox = connectSearchBox(SearchBox);

const AlgoliaHitsList = connectHits<AlgoliaHitsListProps, any>(HitsList);

interface DeviceHit {
   __position: number;
   _highlightResult: any;
   _snippetResult: any;
   all_titles: Array<string>;
   answers_url: string;
   description: string;
   device: string;
   doctype: string;
   flags: Array<any>;
   has_image: number;
   headings: Array<string>;
   image_url: string;
   langid: string;
   modified_date: number;
   namespace: string;
   objectID: string;
   objectType: string;
   parts_url: null | string;
   public: number;
   score_device_posts: number;
   score_wiki_views: number;
   site: string;
   tags: Array<any>;
   title: string;
   url: string;
   userid: number;
   wikiid: number;
}
